const SENSITIVE_KEY =
  /(token|authorization|secret|password|code_verifier|id_token|assertion|api[-_]?key|x-tax-api-key)/i;
const BEARER = /\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi;
const JWT = /\beyJ[A-Za-z0-9._-]{10,}/g;
const TAX_API_KEY = /\bbctax_(?:dev|stage|prod)_[0-9a-f-]{36}_[A-Za-z0-9_-]{20,}/gi;

/**
 * Anything that reaches a console, a breadcrumb or an error report goes
 * through here first. Access tokens, id tokens and PKCE verifiers must never
 * appear in observable output.
 */
export function redact(value: unknown, depth = 0): unknown {
  if (depth > 6) return "[depth-limit]";

  if (typeof value === "string") {
    return value
      .replace(BEARER, "Bearer [redacted]")
      .replace(JWT, "[redacted-jwt]")
      .replace(TAX_API_KEY, "[redacted-api-key]");
  }

  if (Array.isArray(value)) {
    return value.map((entry) => redact(entry, depth + 1));
  }

  if (value instanceof Error) {
    return { name: value.name, message: redact(value.message, depth + 1) };
  }

  if (value && typeof value === "object") {
    const output: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      output[key] = SENSITIVE_KEY.test(key) ? "[redacted]" : redact(entry, depth + 1);
    }
    return output;
  }

  return value;
}

/**
 * The only logging entry point in the app. Using `console` directly anywhere
 * else bypasses redaction, so screens and hooks call this instead.
 */
export function logDiagnostic(scope: string, detail: unknown): void {
  if (import.meta.env.MODE === "test") return;
  console.info(`[tax-web:${scope}]`, redact(detail));
}
